import React, { useState, useCallback } from 'react';
import {
  View,
  StyleSheet,
  TouchableOpacity,
  Linking,
  ActivityIndicator,
  StatusBar,
} from 'react-native';
import { Text } from '@/components/common/text';
import { RouteProp, useRoute, useNavigation } from '@react-navigation/native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { WebView } from 'react-native-webview';
import Icon from 'react-native-vector-icons/Ionicons';
import { RootStackParamList } from '@/navigation/RootNavigator';
import { showAlert } from '@/utils/common/alert';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { REDESIGN_TYPOGRAPHY, REDESIGN_SPACING } from '@/constants/residents/homeConstants';
import { GlassCard } from '@/components/residents/redesign';

type WebViewRouteProp = RouteProp<RootStackParamList, 'WebView'>;

const formatUrl = (urlString: string): string => {
  if (urlString.startsWith('http://') || urlString.startsWith('https://')) {
    return urlString;
  }
  return `https://${urlString}`;
};

export const WebViewScreen: React.FC = () => {
  const { colors } = useThemeColors();
  const route = useRoute<WebViewRouteProp>();
  const navigation = useNavigation();
  const { url, title } = route.params;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const handleBack = useCallback(() => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    }
  }, [navigation]);

  const handleOpenInBrowser = useCallback(async () => {
    try {
      const formattedUrl = formatUrl(url);
      const supported = await Linking.canOpenURL(formattedUrl);
      if (supported) {
        await Linking.openURL(formattedUrl);
      } else {
        showAlert('Error', 'Cannot open this URL');
      }
    } catch {
      showAlert('Error', 'Failed to open URL');
    }
  }, [url]);

  const handleLoadStart = useCallback(() => {
    setLoading(true);
    setError(false);
  }, []);

  const handleLoadEnd = useCallback(() => {
    setLoading(false);
  }, []);

  const handleError = useCallback(() => {
    setLoading(false);
    setError(true);
  }, []);

  if (error) {
    return (
      <SafeAreaView style={[styles.container, { backgroundColor: colors.bg.base }]} edges={['top']}>
        <StatusBar barStyle="light-content" />
        <View style={[styles.headerBar, { borderBottomColor: colors.separator }]}>
          <TouchableOpacity onPress={handleBack} style={[styles.headerButton, { backgroundColor: colors.glass.buttonBg, borderColor: colors.glass.buttonBorder }]}>
            <Icon name="chevron-back" size={22} color={colors.text.primary} />
          </TouchableOpacity>
          <Text style={[styles.headerTitle, { color: colors.text.primary }]} numberOfLines={2}>{title}</Text>
          <TouchableOpacity onPress={handleOpenInBrowser} style={[styles.headerButton, { backgroundColor: colors.glass.buttonBg, borderColor: colors.glass.buttonBorder }]}>
            <Icon name="share-outline" size={20} color={colors.text.primary} />
          </TouchableOpacity>
        </View>
        <View style={styles.errorContainer}>
          <GlassCard borderRadius={20} padding={32}>
            <View style={styles.errorContent}>
              <Icon name="alert-circle-outline" size={48} color={colors.accent.red} />
              <Text style={[styles.errorTitle, { color: colors.text.primary }]}>Unable to Load Page</Text>
              <Text style={[styles.errorText, { color: colors.text.muted }]}>Please check your internet connection and try again.</Text>
              <TouchableOpacity style={styles.retryButton} onPress={handleOpenInBrowser}>
                <Text style={styles.retryButtonText}>Open in Browser</Text>
              </TouchableOpacity>
            </View>
          </GlassCard>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: colors.bg.base }]} edges={['top']}>
      <StatusBar barStyle="light-content" />
      <View style={[styles.headerBar, { borderBottomColor: colors.separator }]}>
        <TouchableOpacity onPress={handleBack} style={[styles.headerButton, { backgroundColor: colors.glass.buttonBg, borderColor: colors.glass.buttonBorder }]}>
          <Icon name="chevron-back" size={22} color={colors.text.primary} />
        </TouchableOpacity>
        <Text style={[styles.headerTitle, { color: colors.text.primary }]} numberOfLines={2}>{title}</Text>
        <TouchableOpacity onPress={handleOpenInBrowser} style={[styles.headerButton, { backgroundColor: colors.glass.buttonBg, borderColor: colors.glass.buttonBorder }]}>
          <Icon name="share-outline" size={20} color={colors.text.primary} />
        </TouchableOpacity>
      </View>
      {loading && (
        <View style={[styles.loadingContainer, { backgroundColor: colors.bg.base }]}>
          <ActivityIndicator size="large" color={PRIMITIVE_COLORS.resioBlue} />
        </View>
      )}
      <WebView
        source={{ uri: formatUrl(url) }}
        style={styles.webview}
        onLoadStart={handleLoadStart}
        onLoadEnd={handleLoadEnd}
        onError={handleError}
        javaScriptEnabled={true}
        domStorageEnabled={true}
        startInLoadingState={true}
        automaticallyAdjustContentInsets={true}
        contentInsetAdjustmentBehavior="automatic"
        allowsBackForwardNavigationGestures={true}
      />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  headerBar: {
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: 48,
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderBottomWidth: 1,
  },
  headerButton: {
    width: 40,
    height: 40,
    borderRadius: 10,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTitle: {
    ...REDESIGN_TYPOGRAPHY.cardTitle,
    flex: 1,
    textAlign: 'center',
    marginHorizontal: 8,
  },
  webview: {
    flex: 1,
  },
  loadingContainer: {
    position: 'absolute',
    top: 48,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1,
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: REDESIGN_SPACING.horizontalPadding,
  },
  errorContent: {
    alignItems: 'center',
    gap: 12,
  },
  errorTitle: {
    ...REDESIGN_TYPOGRAPHY.sectionTitle,
    textAlign: 'center',
  },
  errorText: {
    ...REDESIGN_TYPOGRAPHY.body,
    textAlign: 'center',
  },
  retryButton: {
    backgroundColor: PRIMITIVE_COLORS.resioBlue,
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: REDESIGN_SPACING.buttonRadius,
    marginTop: 8,
  },
  retryButtonText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '600',
    textAlign: 'center',
  },
});
